import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["canvas", "input", "error"]

  connect() {
    this.form = this.element
    this.canvas = this.canvasTarget
    this.input = this.inputTarget
    this.error = this.errorTarget
    this.drawing = false
    this.hasInk = false
    this.boundValidate = this.validate.bind(this)
    this.form.addEventListener("submit", this.boundValidate)
    this.configureContext(window.devicePixelRatio || 1)
    this.resizeCanvas()
    this.resizeObserver = new ResizeObserver(() => this.resizeCanvas())
    this.resizeObserver.observe(this.canvas)
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.boundValidate)
    this.resizeObserver?.disconnect()
  }

  start(event) {
    this.drawing = true
    const point = this.pointFor(event)

    this.capturePointer(event.pointerId)
    this.context.beginPath()
    this.context.arc(point.x, point.y, this.context.lineWidth / 2, 0, Math.PI * 2)
    this.context.fillStyle = this.context.strokeStyle
    this.context.fill()
    this.context.beginPath()
    this.context.moveTo(point.x, point.y)
    this.hasInk = true
    this.store()
    this.hideError()
  }

  draw(event) {
    if (!this.drawing) return
    const point = this.pointFor(event)
    this.context.lineTo(point.x, point.y)
    this.context.stroke()
    this.hasInk = true
    this.store()
    this.hideError()
  }

  stop(event) {
    if (!this.drawing) return
    this.drawing = false
    this.releasePointer(event.pointerId)
    this.store()
  }

  clear() {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.hasInk = false
    this.input.value = ""
    this.hideError()
  }

  validate(event) {
    if (event.submitter?.dataset.skipSignature === "true") return
    if (this.hasInk && this.input.value) return

    event.preventDefault()
    this.error.removeAttribute("hidden")
    this.canvas.focus()
  }

  store() {
    this.input.value = this.hasInk ? this.canvas.toDataURL("image/png") : ""
  }

  hideError() {
    this.error.setAttribute("hidden", "")
  }

  capturePointer(pointerId) {
    if (typeof this.canvas.setPointerCapture !== "function") return

    try {
      this.canvas.setPointerCapture(pointerId)
    } catch {
      // Older iOS versions can reject capture while still delivering pointer events.
    }
  }

  releasePointer(pointerId) {
    if (typeof this.canvas.hasPointerCapture !== "function" || typeof this.canvas.releasePointerCapture !== "function") return

    try {
      if (this.canvas.hasPointerCapture(pointerId)) this.canvas.releasePointerCapture(pointerId)
    } catch {
      // The pointer may already have been released after cancellation.
    }
  }

  pointFor(event) {
    const rect = this.canvas.getBoundingClientRect()
    const x = Math.max(0, Math.min(this.canvas.clientWidth, event.clientX - rect.left - this.canvas.clientLeft))
    const y = Math.max(0, Math.min(this.canvas.clientHeight, event.clientY - rect.top - this.canvas.clientTop))

    return {
      x: x * (this.canvas.width / this.canvas.clientWidth),
      y: y * (this.canvas.height / this.canvas.clientHeight)
    }
  }

  resizeCanvas() {
    const pixelRatio = window.devicePixelRatio || 1
    const width = Math.max(1, Math.round(this.canvas.clientWidth * pixelRatio))
    const height = Math.max(1, Math.round(this.canvas.clientHeight * pixelRatio))
    if (this.canvas.width === width && this.canvas.height === height) return

    const previous = document.createElement("canvas")
    previous.width = this.canvas.width
    previous.height = this.canvas.height
    if (this.hasInk) previous.getContext("2d").drawImage(this.canvas, 0, 0)

    this.canvas.width = width
    this.canvas.height = height
    this.configureContext(pixelRatio)

    if (this.hasInk && previous.width > 0 && previous.height > 0) {
      const scale = Math.min(width / previous.width, height / previous.height)
      const scaledWidth = previous.width * scale
      const scaledHeight = previous.height * scale
      this.context.drawImage(previous, (width - scaledWidth) / 2, (height - scaledHeight) / 2, scaledWidth, scaledHeight)
      this.store()
    }
  }

  configureContext(pixelRatio) {
    this.context = this.canvas.getContext("2d")
    this.context.lineCap = "round"
    this.context.lineJoin = "round"
    this.context.lineWidth = 3 * pixelRatio
    this.context.strokeStyle = "#2d302e"
  }
}
